import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { getSessionStorageValue, setSessionStorageValue, removeSessionStorageValue, STORAGE_KEYS } from '../../Utils/SessionStorage';

const BookingSessionContext = createContext();

export const BookingSessionProvider = ({ children }) => {
  const [bookingID, setBookingIDState] = React.useState(
    () => getSessionStorageValue(STORAGE_KEYS.BOOKING_ID) || null
  );

  const [uuid, setUuidState] = React.useState(
    () => getSessionStorageValue(STORAGE_KEYS.BOOKING_UUID) || null
  );

  const [occupiedTime, setOccupiedTimeState] = React.useState(
    () => getSessionStorageValue(STORAGE_KEYS.OCCUPIED_TIME) || 0
  );

  const [selectedDate, setSelectedDateState] = React.useState(
    () => getSessionStorageValue(STORAGE_KEYS.DATE) || ''
  );

  const [selectedTime, setSelectedTimeState] = React.useState(
    () => getSessionStorageValue(STORAGE_KEYS.TIME) || ''
  );

  const [isBookingDone, setIsBookingDoneState] = React.useState(() => {
    const stored = getSessionStorageValue(STORAGE_KEYS.BOOKING_DONE);
    const initial = stored ?? false;
    // Auto-save initial value if not already in sessionStorage
    if (stored === null) {
      setSessionStorageValue(STORAGE_KEYS.BOOKING_DONE, initial);
    }
    return initial;
  });

  const [bookingUpdate, setBookingUpdateState] = React.useState(() => {
    const stored = getSessionStorageValue(STORAGE_KEYS.BOOKING_UPDATE);
    const initial = stored ?? false;
    if (stored === null) {
      setSessionStorageValue(STORAGE_KEYS.BOOKING_UPDATE, initial);
    }
    return initial;
  });

  const [rescheduleCount, setRescheduleCountState] = React.useState(() => {
    const stored = getSessionStorageValue(STORAGE_KEYS.RESCHEDULE_COUNT);
    const initial = stored ?? 0;
    if (stored === null) {
      setSessionStorageValue(STORAGE_KEYS.RESCHEDULE_COUNT, initial);
    }
    return initial;
  });

  const [rescheduleMaxLimit, setRescheduleMaxLimitState] = React.useState(() => {
    return getSessionStorageValue(STORAGE_KEYS.RESCHEDULE_MAX_LIMIT, null);
  });

  const [reschedulingTimeLimit, setReschedulingTimeLimitState] = React.useState(() => {
    return getSessionStorageValue(STORAGE_KEYS.RESCHEDULE_TIME_LIMIT, null);
  });

  const setBookingID = useCallback((id) => {
    setBookingIDState(id);
    if (id === null) {
      setSessionStorageValue(STORAGE_KEYS.BOOKING_ID, null);
    } else {
      setSessionStorageValue(STORAGE_KEYS.BOOKING_ID, id);
    }
  }, []);

  const setUuid = useCallback((u) => {
    setUuidState(u);
    if (u === null) {
      removeSessionStorageValue(STORAGE_KEYS.BOOKING_UUID);
    } else {
      setSessionStorageValue(STORAGE_KEYS.BOOKING_UUID, u);
    }
  }, []);

  const setOccupiedTime = useCallback((time) => {
    setOccupiedTimeState(time);
    setSessionStorageValue(STORAGE_KEYS.OCCUPIED_TIME, time);
  }, []);

  const setSelectedDate = useCallback((date) => {
    setSelectedDateState(date);
  }, []);

  const setSelectedTime = useCallback((time) => {
    setSelectedTimeState(time);
    if (time === '' || time === null) {
      setSessionStorageValue(STORAGE_KEYS.TIME, null);
    } else {
      setSessionStorageValue(STORAGE_KEYS.TIME, time);
    }
  }, []);

  const setIsBookingDone = useCallback((isDone) => {
    setIsBookingDoneState(isDone);
    setSessionStorageValue(STORAGE_KEYS.BOOKING_DONE, isDone);
  }, []);

  const setBookingUpdate = useCallback((value) => {
    setBookingUpdateState(value);
    setSessionStorageValue(STORAGE_KEYS.BOOKING_UPDATE, value);
  }, []);

  const setRescheduleCount = useCallback((count) => {
    setRescheduleCountState(count);
    setSessionStorageValue(STORAGE_KEYS.RESCHEDULE_COUNT, count);
  }, []);

  const setRescheduleMaxLimit = useCallback((limit) => {
    setRescheduleMaxLimitState(limit);
    setSessionStorageValue(STORAGE_KEYS.RESCHEDULE_MAX_LIMIT, limit);
  }, []);

  const setReschedulingTimeLimit = useCallback((limit) => {
    setReschedulingTimeLimitState(limit);
    setSessionStorageValue(STORAGE_KEYS.RESCHEDULE_TIME_LIMIT, limit);
  }, []);

  const value = useMemo(
    () => ({
      bookingID,
      setBookingID,
      uuid,
      setUuid,
      occupiedTime,
      setOccupiedTime,
      selectedDate,
      setSelectedDate,
      selectedTime,
      setSelectedTime,
      isBookingDone,
      setIsBookingDone,
      bookingUpdate,
      setBookingUpdate,
      rescheduleCount,
      setRescheduleCount,
      rescheduleMaxLimit,
      setRescheduleMaxLimit,
      reschedulingMaxLimit: rescheduleMaxLimit,
      reschedulingTimeLimit,
      setReschedulingTimeLimit,
    }),
    [bookingID, setBookingID, uuid, setUuid, occupiedTime, setOccupiedTime, selectedDate, setSelectedDate, selectedTime, setSelectedTime, isBookingDone, setIsBookingDone, bookingUpdate, setBookingUpdate, rescheduleCount, setRescheduleCount, rescheduleMaxLimit, setRescheduleMaxLimit, reschedulingTimeLimit, setReschedulingTimeLimit]
  );

  return (
    <BookingSessionContext.Provider value={value}>
      {children}
    </BookingSessionContext.Provider>
  );
};

export const useBookingSession = () => {
  const context = useContext(BookingSessionContext);
  if (!context) {
    throw new Error('useBookingSession must be used within BookingSessionProvider');
  }
  return context;
};

export default BookingSessionContext;
